import { ComponentProps, ReactNode, useCallback } from 'react';

import { getFormattedAmountUI } from '@axo/shared/util/formatters';
import { Icon } from '@axo/ui-core/components/Icon';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Text } from '@axo/ui-core/components/typography';
import clsx from 'clsx';
import { useExtendedLoanOverviewContext } from '../../context/ExtendedLoanOverviewContext/ExtendedLoanOverviewContext';
import { TBlockType } from '../../ExtendedLoanOverview.types';
import Detail from '../Detail/Detail';
import SliderInput from '../SliderInput/SliderInput';
import SteppedNumberInput from '../SteppedNumberInput/SteppedNumberInput';
import styles from './LoanOverviewBlock.module.scss';

export const variants = ['default', 'info'] as const;

export type TLoanOverviewBlockProps = {
  type?: TBlockType;
  title?: ReactNode | string;
  isEditable?: boolean;
  value?: number;
  valueAsNode?: ReactNode;
  labelSuffix?: string;
  editType?: 'slider' | 'steppedNumberInput' | 'toggle';
  details?: Omit<ComponentProps<typeof Detail>, 'onChangeComplete'>[];
  minValue?: number;
  maxValue?: number;
  step?: number;
  isEditMode?: boolean;
  warningMessage?: ReactNode;
  variant?: (typeof variants)[number];
  sliderConfig?: Partial<ComponentProps<typeof SliderInput>>;
  isInfoButtonVisible?: boolean;
  description?: ReactNode;
  className?: string;
  isTotalAmountVisible?: boolean;
  isPermanentEdit?: boolean;
  onChange?: (newValue: number) => void;
  onEnableEditModeClick?: (mode: TBlockType | undefined) => void;
  onInfoButtonClick?: () => void;
};

const LoanOverviewBlock = ({
  type,
  title = '',
  value = undefined,
  valueAsNode,
  isEditable = true,
  labelSuffix = '',
  editType = 'slider',
  minValue = 0,
  maxValue = 10000,
  step = 500,
  isEditMode = false,
  warningMessage,
  details,
  variant = 'default',
  sliderConfig,
  isInfoButtonVisible,
  description,
  className,
  isTotalAmountVisible = true,
  isPermanentEdit = false,
  onInfoButtonClick,
  onEnableEditModeClick,
  onChange,
}: TLoanOverviewBlockProps) => {
  const { currency } = useExtendedLoanOverviewContext();

  const handleEditModeClick = useCallback(() => {
    onEnableEditModeClick?.(type);
  }, [onEnableEditModeClick, type]);

  const handleCloseEditMode = useCallback(() => {
    onEnableEditModeClick?.(undefined);
  }, [onEnableEditModeClick]);

  const handleOnChange = useCallback(
    (newValue: number) => {
      onChange?.(newValue);
    },
    [onChange]
  );

  const _isEditMode = isPermanentEdit || isEditMode;

  const valueFormatter = useCallback(
    (value: number) => (
      <div className={styles.steppedNumberInputValue}>
        <Text>
          {title}{' '}
          <Text size={'l'}>
            <b>{`${value} ${labelSuffix}`}</b>
          </Text>
        </Text>
        {/* TODO: Implement Recommended badge */}
        {/*<Text size={'xs'} className={styles.badge}>Recommended</Text>*/}
      </div>
    ),
    [labelSuffix, title]
  );

  const hasDetails = !!details?.length;

  const isEditButtonVisible = isEditable && !_isEditMode && !isPermanentEdit;
  const isConfirmButtonVisible = isEditable && _isEditMode && !isPermanentEdit;

  const showSlider = !hasDetails && editType === 'slider' && _isEditMode;
  const showSteppedNumberInput =
    !hasDetails && editType === 'steppedNumberInput' && _isEditMode;

  return (
    <div
      className={clsx(styles.loanOverviewBlock, className)}
      data-variant={variant}
      data-type={type}
    >
      <Stack direction={'h'} gap={'3xs'} className={styles.titleContainer}>
        <Stack direction={'h'} gap={'3xs'} className={styles.left}>
          {typeof title === 'string' ? (
            <Text className={styles.title}>{title}</Text>
          ) : (
            title
          )}
          {isInfoButtonVisible && (
            <div onClick={onInfoButtonClick} data-testid="elo-info-button">
              <Icon name="info-solid" size="s" className={styles.icon} />
            </div>
          )}
        </Stack>
        {isTotalAmountVisible && (
          <Stack direction={'h'} gap={'3xs'} className={styles.right}>
            {value !== undefined && (
              <Text className={styles.value}>
                {`${getFormattedAmountUI(value)} ${labelSuffix}`}
              </Text>
            )}

            {valueAsNode}

            {isEditButtonVisible && (
              <div onClick={handleEditModeClick}>
                <Icon
                  name="pencil-simple-line"
                  size="xs"
                  className={styles.icon}
                />
              </div>
            )}
            {isConfirmButtonVisible && (
              <div onClick={handleCloseEditMode} data-testid="elo-check-button">
                <Icon
                  name="check-fat-solid"
                  size="xs"
                  className={styles.icon}
                />
              </div>
            )}
          </Stack>
        )}
      </Stack>

      {showSteppedNumberInput && value !== undefined && (
        <SteppedNumberInput
          value={value}
          onChange={handleOnChange}
          minValue={minValue}
          maxValue={maxValue}
          valueFormatter={valueFormatter}
        />
      )}
      {description}
      {warningMessage}
      {showSlider && value !== undefined && (
        <SliderInput
          value={value}
          max={maxValue}
          min={minValue}
          onChange={handleOnChange}
          step={step}
          currency={currency}
          className={styles.slider}
          {...sliderConfig}
        />
      )}
      {hasDetails && (
        <div className={styles.details}>
          {details?.map((detail, index) => (
            <Detail
              key={detail.label.concat(index.toString())}
              {...detail}
              isEditMode={_isEditMode}
              editType={editType}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LoanOverviewBlock;
